import toast from 'react-hot-toast';

import { encrypt } from '@repo/services/fingerprint';
import { ErrorResponseType } from '@repo/services/hooks';
import { BankAccountVerifyBody, BankAccountVerifyFormikValues } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { FormikProps } from 'formik';
import { t } from 'i18next';

import { app } from 'services/firebase';
import { REVENUES_KEY } from 'services/hooks/revenue';
import { MY_PROFILE_KEY, useGetMyProfile } from 'services/hooks/user';
import {
  VERIFICATION_KEY,
  useCreateBankAccountVerification,
  useGetBankAccountVerifications,
  useGetBankList,
  useGetUsersIdentityVerifications,
  useUpdateBankAccountVerification,
} from 'services/hooks/verification';
import { useModalStackStore } from 'store/modalStackStore';
import { LOADING_KEY } from 'utils/promise';

import { useCheckBankAccountVerificationsEnable } from 'pages/dashboard/hooks/useCheckBankAccountVerifications';
import { controlBankAccountVerifyErrorCode } from 'pages/dashboard/utils/controlBankAccountVerifyErrorCode';
import { handleOpenExitConfirmationModal } from 'pages/dashboard/utils/handleOpenExitConfirmationModal';

import ModificationButton from '../shared/ModificationButton';
import VerificationButton, { BaseVerificationProps } from '../shared/VerificationButton';

type BankAccountVerificationProps = {
  isVerificationEnabled: boolean;
} & BaseVerificationProps;

export const BankAccountVerificationButton = ({
  isVerificationEnabled,
  isVerificationCompleted,
}: BankAccountVerificationProps) => {
  const { data: profileData } = useGetMyProfile();

  const userId = profileData?.id;
  const { data: usersIdentityVerifications } = useGetUsersIdentityVerifications(userId);
  const isBankAccountVerificationEnabled = useCheckBankAccountVerificationsEnable();
  const { data: bankAccountVerifications } = useGetBankAccountVerifications(userId, isBankAccountVerificationEnabled);

  const { data: bankList } = useGetBankList(isBankAccountVerificationEnabled);
  const { mutateAsync: postBankAccountVerification } = useCreateBankAccountVerification(userId);
  const queryClient = useQueryClient();

  const { openModal, closeModal } = useModalStackStore();

  const handleRequestBankAccountVerification = async (verification: FormikProps<BankAccountVerifyFormikValues>) => {
    const { name, userRegistrationNumber, bankCode, accountNumber, accountHolder } = verification.values;

    // 암호화 처리
    const encryptedName = (await encrypt(app, name)) ?? '';
    const encryptedUserRegistrationNumber = (await encrypt(app, userRegistrationNumber)) ?? '';
    const encryptedAccountNumber = (await encrypt(app, accountNumber)) ?? '';

    // 서버에 데이터 전송
    await postBankAccountVerification(
      {
        name: encryptedName,
        userRegistrationNumber: encryptedUserRegistrationNumber,
        bankCode,
        accountNumber: encryptedAccountNumber,
        accountHolder,
      },
      {
        onSuccess: () => {
          toast.success('인증에 성공했습니다.');
          queryClient.invalidateQueries(VERIFICATION_KEY.bankAccount);
          queryClient.invalidateQueries(MY_PROFILE_KEY);
          queryClient.invalidateQueries(REVENUES_KEY.lists());
          closeModal();
        },
        onError: (error) => {
          const {
            response: {
              data: { code },
            },
          } = error as ErrorResponseType;
          // Error Control
          controlBankAccountVerifyErrorCode(code, verification);
        },
      }
    );
  };

  const handleOpenBankAccountVerificationModal = () => {
    if (!profileData?.role) return;
    // const isBubbleupUser = profileData.role.code === 'BUBBLEUP_USER';
    // if (!isBubbleupUser) return;
    // if (!profileData.bankAccountVerifiedAt) return;
    if (usersIdentityVerifications) {
      openModal(
        <BankAccountVerifyBody
          isUpdateBankAccount={false}
          form={{
            name: usersIdentityVerifications.name, // 이름
            userRegistrationNumber: bankAccountVerifications?.userRegistrationNumber.replace('-', '') ?? '', // 주민등록번호
            bankCode: bankAccountVerifications?.bankCode, // 은행
            accountNumber: bankAccountVerifications?.accountNumber.replaceAll('*', '•'), // 계좌 번호
            accountHolder: usersIdentityVerifications?.name, // 예금주
          }}
          bankList={bankList}
          handleBankAccount={handleRequestBankAccountVerification}
          handleOpenExitConfirmationModal={handleOpenExitConfirmationModal}
          closeModal={closeModal}
        />,
        {
          modalWrapperStyle: {
            // width: '400px',
            maxHeight: '630px',
            padding: '10px 24px 30px',
          },
        }
      );
    }
  };

  // Email 인증과 Identity 인증을 완료한 경우에만 isDisabled가 false
  return (
    <VerificationButton
      loadingKey={LOADING_KEY.BANK_VERIFICATIONS}
      isDisabled={!isVerificationEnabled}
      isCompleted={isVerificationCompleted}
      onClick={handleOpenBankAccountVerificationModal}
    />
  );
};

export const BankAccountModificationButton = () => {
  const { openModal, closeModal } = useModalStackStore();
  const { data: profileData } = useGetMyProfile();
  const queryClient = useQueryClient();

  const userId = profileData?.id;
  const isBankAccountVerificationEnabled = useCheckBankAccountVerificationsEnable();
  const { data: bankAccountVerifications } = useGetBankAccountVerifications(userId, isBankAccountVerificationEnabled);
  const { data: usersIdentityVerifications } = useGetUsersIdentityVerifications(userId);
  const { data: bankList } = useGetBankList(isBankAccountVerificationEnabled);
  const { mutateAsync: postBankAccountUpdate } = useUpdateBankAccountVerification(userId);

  const handleRequestBankAccountModification = async (verification: FormikProps<BankAccountVerifyFormikValues>) => {
    const { bankCode, accountNumber } = verification.values;
    // 암호화 처리
    const encryptedAccountNumber = (await encrypt(app, accountNumber)) ?? '';

    // 계좌 변경 요청
    await postBankAccountUpdate(
      {
        bankCode,
        accountNumber: encryptedAccountNumber,
      },
      {
        onSuccess: () => {
          toast.success('변경에 성공했습니다.');
          queryClient.invalidateQueries(VERIFICATION_KEY.bankAccount);
          queryClient.invalidateQueries(MY_PROFILE_KEY);
          queryClient.invalidateQueries(REVENUES_KEY.lists());
          closeModal();
        },
        onError: (error) => {
          const {
            response: {
              data: { code },
            },
          } = error as ErrorResponseType;
          // Error Control
          controlBankAccountVerifyErrorCode(code, verification);
        },
      }
    );
  };

  const handleOpenBankAccountModificationModal = () => {
    if (!profileData?.role) return;
    if (!userId) return;

    // const isBubbleupUser = profileData.role.code === 'BUBBLEUP_USER';
    // if (!isBubbleupUser) return;
    if (!profileData.bankAccountVerifiedAt) return;
    // usersIdentityVerifications

    if (usersIdentityVerifications && bankAccountVerifications) {
      openModal(
        <BankAccountVerifyBody
          form={{
            name: usersIdentityVerifications?.name,
            userRegistrationNumber: bankAccountVerifications?.userRegistrationNumber.replace('-', '') ?? '', // 주민등록번호
            bankCode: bankAccountVerifications?.bankCode,
            accountNumber: bankAccountVerifications?.accountNumber ?? '',
            accountHolder: usersIdentityVerifications?.name,
          }}
          bankList={bankList}
          handleBankAccount={handleRequestBankAccountModification}
          closeModal={closeModal}
          handleOpenExitConfirmationModal={handleOpenExitConfirmationModal}
          isUpdateBankAccount={true}
        />,
        {
          modalWrapperStyle: {
            // width: '400px',
            maxHeight: '630px',
            padding: '10px 24px 30px',
          },
        }
      );
    }
  };

  return (
    <ModificationButton loadingKey={LOADING_KEY.BANK_VERIFICATIONS} onClick={handleOpenBankAccountModificationModal}>
      {t('chat.rewards_creator_information_edit')}
    </ModificationButton>
  );
};
